import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Header from "src/components/wizard/Header";
import ScanditScanner from "src/components/barcode/ScanditScanner/ScanditScanner";
import MoveLocationsImage from "../../../../assets/images/move_locations.webp";
import useAuth from "src/hooks/useAuth";
import useQueryParams from "src/hooks/useQueryParams";
import ReceiverInventoryMove from "./ReceiverInventoryMove";
import MoveByItemIdModal from "./MoveByItemIdModal";
import MoveBySearchModal from "./MoveBySearchModal";
import ManualSearchModal from "./ManualSearchModal";
import MoveEmptyMobileLocationModal from "./MoveEmptyMobileLocationModal";
import ScanActions from "./ScanActions";
import LoadingDialog from "./LoadingDialog";
import InternalWorkOrderGuidedWizardModal from "src/views/inventory/requests/orders/internal-work-order-guided-wizard/InternalWorkOrderGuidedWizardModal";
import OutboundOrderGuidedWizardModal from "src/views/inventory/requests/orders/outbound-order-guided-wizard/OutboundOrderGuidedWizardModal";
import { INTERNAL_WORK_ORDER_QUERY, INVENTORY_REQUEST_QUERY, GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";

const parseShipmentIdFromCode = code => {
  const pdfMatch = code?.match(/shipment_pdfs\/(\d+)/);
  if (pdfMatch) return pdfMatch[1];

  const labelMatch = code?.match(/shipment_items\/label_dashboard\/\d+/);
  if (labelMatch) {
    try {
      return new URL(code).searchParams.get("shipment_id");
    } catch {
      return null;
    }
  }

  return null;
};

const parseInventoryRequestIdentifierFromCode = code => {
  const pdfMatch = code?.match(/inventory_request_pdfs\/(\d+)/);
  if (pdfMatch) return pdfMatch[1];

  const hubMatch = code?.match(/\/ir\/([^/?#]+)\/hub(?:[/?#]|$)/);
  if (hubMatch) return hubMatch[1];

  // Support the short-lived QR format that used a delivery path with the IR id.
  const legacyDeliveryMatch = code?.match(/\/deliveries\/(\d+)(?:[/?#]|$)/);
  if (legacyDeliveryMatch) return legacyDeliveryMatch[1];

  return null;
};

const MoveLocationsModal = ({ open, onClose, clientId, projectId, onSearchAllCustomers }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useQueryParams();
  const [openScan, setOpenScan] = useState(false);
  const manualSearch = params.get("manualSearch");
  const matchMode = params.get("matchMode");
  const [openEmptyMobileLocation, setOpenEmptyMobileLocation] = useState(false);

  const code = params.get("code");

  const setCode = (newCode, nextMatchMode = null) => {
    if (newCode) {
      params.set("code", newCode);
      if (nextMatchMode) {
        params.set("matchMode", nextMatchMode);
      } else {
        params.delete("matchMode");
      }
    } else {
      params.delete("code");
      params.delete("matchMode");
    }
    navigate({ search: params.toString() });
  };

  // Get internal work order ID from URL params
  const internalWorkOrderGuidedWizardId = params.get("internalWorkOrderGuidedWizard");
  const outboundOrderGuidedWizardId = params.get("outboundOrderGuidedWizard");
  const shipmentScanId = params.get("shipmentScan");

  // Fetch internal work order when guided wizard is open
  const { data: { internalWorkOrder } = {}, loading: loadingInternalWorkOrder } = useQuery(
    INTERNAL_WORK_ORDER_QUERY,
    {
      variables: { id: internalWorkOrderGuidedWizardId },
      skip: !internalWorkOrderGuidedWizardId,
      fetchPolicy: "cache-and-network",
    }
  );

  // Fetch inventory request when guided wizard is open
  const { data: { inventoryRequest } = {}, loading: loadingInventoryRequest } = useQuery(
    INVENTORY_REQUEST_QUERY,
    {
      variables: { id: outboundOrderGuidedWizardId },
      skip: !outboundOrderGuidedWizardId,
      fetchPolicy: "cache-and-network",
    }
  );

  // Fetch shipment when a shipment QR code is scanned
  const { data: { shipment: scannedShipment } = {}, loading: loadingShipmentScan } = useQuery(
    GET_SHIPMENT_QUERY,
    {
      variables: { id: shipmentScanId },
      skip: !shipmentScanId,
      fetchPolicy: "cache-and-network",
    }
  );

  useEffect(() => {
    if (!scannedShipment) return;
    const projectId = scannedShipment.project?.id;
    if (!projectId) return;
    params.delete("shipmentScan");
    navigate({
      pathname: `/projects/${projectId}/capture-inventory`,
      search: new URLSearchParams({
        step: "info",
        receiving: "true",
        editShipment: "true",
        shipmentId: scannedShipment.shipmentId,
        id: scannedShipment.id,
      }).toString(),
    });
  }, [scannedShipment, navigate]);

  const handleDetectedCode = (code, nextMatchMode = null) => {
    const trimmedCode = code?.trim();

    // Check if it's an internal work order PDF URL
    const internalWorkOrderMatch = trimmedCode?.match(/internal_work_order_pdfs\/(\d+)/);
    if (internalWorkOrderMatch) {
      const workOrderId = internalWorkOrderMatch[1];
      params.set("internalWorkOrderGuidedWizard", workOrderId);
      params.set("step", "details");
      navigate({ search: params.toString() });
      return;
    }

    const inventoryRequestIdentifier = parseInventoryRequestIdentifierFromCode(trimmedCode);
    if (inventoryRequestIdentifier) {
      params.set("outboundOrderGuidedWizard", inventoryRequestIdentifier);
      params.set("step", "details");
      navigate({ search: params.toString() });
      return;
    }

    const shipmentId = parseShipmentIdFromCode(trimmedCode);
    if (shipmentId) {
      params.set("shipmentScan", shipmentId);
      navigate({ search: params.toString() });
      return;
    }

    setCode(trimmedCode, nextMatchMode);
  };

  const handleToggleScan = () => {
    setOpenScan(prev => !prev);
  };

  const handleCloseScan = () => {
    setOpenScan(false);
  };

  const handleClose = () => {
    setCode(null);
  };

  const handleSelect = asset => {
    setCode(asset.itemId);
  };

  const handleManualSearch = () => {
    params.set("manualSearch", "true");
    navigate({ search: params.toString() });
  };

  const handleCloseManualSearch = closeMoveModal => {
    params.delete("code");
    params.delete("manualSearch");
    params.delete("movedAssetIds");
    params.delete("newRackLocation");
    params.delete("newInventoryLocations");
    params.delete("movedAssetIds");
    params.delete("newMobileWarehouseLocation");
    params.delete("matchMode");
    if (closeMoveModal) {
      params.delete("moveModal");
    }
    navigate({ search: params.toString() });
  };

  const handleSearchSubmit = (searchCode, nextMatchMode) => {
    params.delete("manualSearch");
    handleDetectedCode(searchCode, nextMatchMode);
  };

  const handleMoveEmptyMobileLocation = () => {
    setOpenEmptyMobileLocation(true);
  };

  const handleCloseEmptyMobileLocation = () => {
    setOpenEmptyMobileLocation(false);
  };

  const handleInternalWorkOrderWizardClose = () => {
    params.delete("internalWorkOrderGuidedWizard");
    params.delete("step");
    navigate({ search: params.toString() });
  };

  const handleOutboundOrderWizardClose = () => {
    params.delete("outboundOrderGuidedWizard");
    params.delete("step");
    navigate({ search: params.toString() });
  };

  // If internal work order guided wizard is open, render it or show loading
  if (internalWorkOrderGuidedWizardId) {
    if (loadingInternalWorkOrder || !internalWorkOrder) {
      return <LoadingDialog onClose={handleInternalWorkOrderWizardClose} />;
    }
    return (
      <InternalWorkOrderGuidedWizardModal
        internalWorkOrder={internalWorkOrder}
        onToggle={handleInternalWorkOrderWizardClose}
      />
    );
  }

  // If outbound order guided wizard is open, render it or show loading
  if (outboundOrderGuidedWizardId) {
    if (loadingInventoryRequest || !inventoryRequest) {
      return <LoadingDialog onClose={handleOutboundOrderWizardClose} />;
    }
    return (
      <OutboundOrderGuidedWizardModal
        inventoryRequest={inventoryRequest}
        onToggle={handleOutboundOrderWizardClose}
      />
    );
  }

  if (shipmentScanId) {
    return <LoadingDialog />;
  }

  if (!!manualSearch) {
    return (
      <ManualSearchModal
        open={!!manualSearch}
        onClose={handleCloseManualSearch}
        onSearch={handleSearchSubmit}
      />
    );
  }

  if (code && code.includes(",")) {
    return <ReceiverInventoryMove code={code} onClose={handleClose} />;
  }

  if (code && /^\d{6}$/.test(code)) {
    return <MoveByItemIdModal code={code} onClose={handleClose} />;
  }

  if (code) {
    return (
      <MoveBySearchModal
        code={code}
        clientId={clientId}
        projectId={projectId}
        onSearchAllCustomers={onSearchAllCustomers}
        matchMode={matchMode}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
      <Box bgcolor="#f4f6f8" position="relative">
        <Header title="Inventory Scan" />
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        <>
          <Box display="flex" justifyContent="center" mt={2}>
            <Box
              component="img"
              src={MoveLocationsImage}
              borderRadius={3}
              width="100%"
              maxWidth={300}
              minHeight="100px"
              alt="Move Locations"
            />
          </Box>
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography maxWidth={300} fontWeight={500} align="center">
              Scan an item barcode or order QR code and I'll take you to the right workflow.
            </Typography>
          </Box>
          <Box mt={4} display="flex" justifyContent="center">
            <ScanActions
              onScan={handleToggleScan}
              onManualSearch={handleManualSearch}
              onMoveEmptyMobileLocation={handleMoveEmptyMobileLocation}
            />
          </Box>
        </>
        {openScan && (
          <ScanditScanner
            onDetected={handleDetectedCode}
            onClose={handleCloseScan}
          />
        )}

        {openEmptyMobileLocation && (
          <MoveEmptyMobileLocationModal onClose={handleCloseEmptyMobileLocation} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MoveLocationsModal;
